/**
 * The ONLY place the wizard URL shape lives.
 *
 * Steps are addressed as `/forms/<slug>?step=<key>` for now. The CDN
 * currently 404s sub-paths such as `/forms/<slug>/<key>` because there is no
 * object at that key in S3 and no edge rewrite yet. Once the CDK edge function
 * rewrites `/forms/<slug>/*` to the form page, switch `stepUrl`/`parseStepKey`
 * to path segments here and nothing else needs to change.
 *
 * A link may also carry answers (docs/forms/self-serve-forms.md, Resuming
 * from a link): one query parameter per answer key, dotted as posted
 * (`home.owner=true&solar.sizeKw=4`), a checkbox answer as a repeated key.
 * It may carry the responses of result steps too, as `results=<base64url of
 * the JSON results object>`, so a resumed quote shows the figures that were
 * emailed rather than a fresh calculation. `step`, `session` and `results`
 * are the engine's own parameters and are never answers.
 */

const STEP_PARAM = 'step'
const SESSION_PARAM = 'session'
const RESULTS_PARAM = 'results'
const RESERVED = [STEP_PARAM, SESSION_PARAM, RESULTS_PARAM]

// Base64url (RFC 4648 §5, no padding) of a UTF-8 string, in browsers, jsdom
// and Node alike.
const toBase64Url = (text: string): string => {
  const utf8 = unescape(encodeURIComponent(text))
  const base64 =
    typeof btoa === 'function' ? btoa(utf8) : Buffer.from(utf8, 'binary').toString('base64')
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

const fromBase64Url = (encoded: string): string => {
  const base64 = encoded.replace(/-/g, '+').replace(/_/g, '/')
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
  const utf8 =
    typeof atob === 'function' ? atob(padded) : Buffer.from(padded, 'base64').toString('binary')
  return decodeURIComponent(escape(utf8))
}

/** `results=` value for a results object; undefined when there is nothing to carry. */
export const encodeLinkResults = (results: Record<string, unknown>): string | undefined =>
  Object.keys(results).length === 0 ? undefined : toBase64Url(JSON.stringify(results))

/**
 * The results object carried by a link, or undefined when absent or not a
 * JSON object. The caller decides which keys to trust.
 */
export const parseLinkResults = (location: {
  readonly search?: string
}): Record<string, unknown> | undefined => {
  const search = location.search ?? ''
  if (!search) return undefined
  const encoded = new URLSearchParams(search).get(RESULTS_PARAM)
  if (!encoded) return undefined
  try {
    const parsed: unknown = JSON.parse(fromBase64Url(encoded))
    return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : undefined
  } catch {
    return undefined
  }
}

/** URL for a given step. The bare base path is also the first visible step. */
export const stepUrl = (basePath: string, key: string): string => {
  const base = basePath.replace(/\/+$/, '') || '/'
  return `${base}?${STEP_PARAM}=${encodeURIComponent(key)}`
}

/**
 * History state every step navigation carries, so the site's global
 * `shouldUpdateScroll` (gatsby-browser.js) can tell a step change from a
 * page change and stay out of it: the wizard scrolls to the top of the form
 * itself (router/scroll.ts). It is a marker, not a saved position.
 */
export const STEP_NAVIGATION_STATE = { wizardStep: true } as const

export const isStepNavigation = (state: unknown): boolean =>
  typeof state === 'object' && state !== null && (state as { wizardStep?: unknown }).wizardStep === true

/** Reads the requested step key from a location-like object. */
export const parseStepKey = (location: {
  readonly search?: string
}): string | undefined => {
  const search = location.search ?? ''
  if (!search) return undefined
  const value = new URLSearchParams(search).get(STEP_PARAM)
  return value ? value : undefined
}

/** Raw answer candidates in a link: every parameter that is not the engine's own. */
export type LinkValues = Record<string, string | ReadonlyArray<string>>

export const parseLinkValues = (location: { readonly search?: string }): LinkValues => {
  const search = location.search ?? ''
  if (!search) return {}
  const params = new URLSearchParams(search)
  const out: LinkValues = {}
  Array.from(new Set(Array.from(params.keys()))).forEach(key => {
    if (RESERVED.includes(key)) return
    const values = params.getAll(key).filter(value => value !== '')
    if (values.length === 0) return
    out[key] = values.length === 1 ? values[0] : values
  })
  return out
}

/**
 * A link that opens `key` with `answers` restored: flat answers keyed by
 * dotted answer key, as `flattenAnswers` produces. Arrays become repeated
 * parameters; everything else is stringified. Objects are skipped. `results`,
 * when given, is carried whole as `results=<base64url JSON>`. Both the site
 * and any endpoint that emails a "back to your quote" link build it this way
 * (docs/forms/ecohub-proxy.openapi.yaml).
 */
export const resumeUrl = (
  basePath: string,
  key: string,
  answers: Record<string, unknown>,
  results?: Record<string, unknown>,
): string => {
  const params = new URLSearchParams()
  params.set(STEP_PARAM, key)
  Object.keys(answers).forEach(name => {
    const value = answers[name]
    if (value === undefined || value === null || value === '') return
    if (Array.isArray(value)) {
      value.forEach(item => params.append(name, String(item)))
      return
    }
    if (typeof value === 'object') return
    params.append(name, String(value))
  })
  const encoded = results ? encodeLinkResults(results) : undefined
  if (encoded) params.set(RESULTS_PARAM, encoded)
  const base = basePath.replace(/\/+$/, '') || '/'
  return `${base}?${params.toString()}`
}
